import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { organizationsApi } from './organizations.api';
import {
  ACTIVE_ORG_STORAGE_KEY,
  ActiveOrgContext,
  orgsQueryKey,
  type ActiveOrgContextValue,
} from './active-org-context';

/**
 * Loads the user's organizations and tracks the active one (persisted to
 * localStorage). Mounted inside the authenticated area only.
 */
export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({ queryKey: orgsQueryKey, queryFn: organizationsApi.list });
  const organizations = useMemo(() => data ?? [], [data]);

  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ORG_STORAGE_KEY),
  );

  const setActiveOrgId = useCallback((id: string) => {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, id);
    setActiveOrgIdState(id);
  }, []);

  // Keep the selection valid: fall back to the first org when the stored id is
  // missing or no longer accessible.
  useEffect(() => {
    if (isLoading) return;
    const first = organizations[0];
    if (!first) return;
    const stillValid = activeOrgId && organizations.some((o) => o.id === activeOrgId);
    if (!stillValid) setActiveOrgId(first.id);
  }, [isLoading, organizations, activeOrgId, setActiveOrgId]);

  const activeOrg = organizations.find((o) => o.id === activeOrgId) ?? null;

  const value: ActiveOrgContextValue = {
    organizations,
    activeOrg,
    activeOrgId: activeOrg?.id ?? null,
    setActiveOrgId,
    isLoading,
  };

  return <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>;
}
